import { useEffect, useMemo, useState } from "react";
import {
  Box, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TableSortLabel, Typography,
} from "@mui/material";

const PAGE_SIZES = [10, 25, 50, 100];
const SIZE_KEY = "grc_page_size";

function storedPageSize() {
  try {
    const n = Number(localStorage.getItem(SIZE_KEY));
    return PAGE_SIZES.includes(n) ? n : 10;
  } catch {
    return 10;
  }
}

// Empty values always sort last, whichever direction is chosen.
function compare(a, b) {
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" });
}

/**
 * Table driven by a column list, with click-to-sort headers and paging.
 * columns: [{ key, label, render?(row), align?, width?, sortValue?(row), sortable?, nowrap? }]
 *   sortValue lets a column sort by something other than its raw value (a tier's rank, a stage's order).
 * defaultSort: { key, dir: "asc" | "desc" }
 * In the "All departments" view the rows carry a department, and a Department column is added automatically.
 */
export default function DataTable({ columns, rows, onRowClick, emptyText = "Nothing to show yet.", defaultSort }) {
  const showDepartment = rows.some((r) => r.department);
  const cols = useMemo(
    () => (showDepartment ? [{ key: "department", label: "Department", nowrap: true }, ...columns] : columns),
    [columns, showDepartment]
  );

  const [sort, setSort] = useState(defaultSort || null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(storedPageSize);

  const sorted = useMemo(() => {
    if (!sort) return rows;
    const col = cols.find((c) => c.key === sort.key);
    if (!col) return rows;
    const value = (r) => (col.sortValue ? col.sortValue(r) : r[col.key]);
    const empty = (v) => v == null || v === "";
    const factor = sort.dir === "desc" ? -1 : 1;
    return [...rows].sort((a, b) => {
      const va = value(a);
      const vb = value(b);
      if (empty(va) && empty(vb)) return 0;
      if (empty(va)) return 1;
      if (empty(vb)) return -1;
      return compare(va, vb) * factor;
    });
  }, [rows, cols, sort]);

  // Go back to page 1 when the list itself changes (a filter, another department) or the order does.
  useEffect(() => setPage(0), [rows, sort]);

  const lastPage = Math.max(0, Math.ceil(sorted.length / pageSize) - 1);
  const current = Math.min(page, lastPage);
  const visible = sorted.slice(current * pageSize, current * pageSize + pageSize);

  function toggleSort(key) {
    setSort((prev) => (prev && prev.key === key ? { key, dir: prev.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));
  }

  function changePageSize(e) {
    const n = Number(e.target.value);
    setPageSize(n);
    setPage(0);
    try {
      localStorage.setItem(SIZE_KEY, String(n));
    } catch {
      /* not remembered */
    }
  }

  return (
    <>
      <TableContainer sx={{ borderRadius: 3, overflowX: "auto" }}>
        <Table size="medium">
          <TableHead>
            <TableRow>
              {cols.map((c) => {
                const sortable = c.sortable !== false;
                const active = sort && sort.key === c.key;
                return (
                  <TableCell
                    key={c.key}
                    align={c.align || "left"}
                    sx={{ width: c.width }}
                    sortDirection={active ? sort.dir : false}
                  >
                    {sortable ? (
                      <TableSortLabel active={active} direction={active ? sort.dir : "asc"} onClick={() => toggleSort(c.key)}>
                        {c.label}
                      </TableSortLabel>
                    ) : (
                      c.label
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {visible.map((row) => (
              <TableRow
                key={row.departmentId != null ? `${row.departmentId}-${row.id}` : row.id}
                hover
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                sx={{ cursor: onRowClick ? "pointer" : "default" }}
              >
                {cols.map((c) => (
                  <TableCell key={c.key} align={c.align || "left"} sx={c.nowrap ? { whiteSpace: "nowrap" } : undefined}>
                    {c.render ? c.render(row) : row[c.key] ?? "—"}
                  </TableCell>
                ))}
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={cols.length}>
                  <Box sx={{ py: 5, textAlign: "center" }}>
                    <Typography color="text.secondary">{emptyText}</Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {rows.length > 0 && (
        <TablePagination
          component="div"
          count={sorted.length}
          page={current}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={changePageSize}
          rowsPerPageOptions={PAGE_SIZES}
          labelRowsPerPage="Rows per page"
        />
      )}
    </>
  );
}

/** Two-line cell: bold main text with a small gray line under it. */
export function TwoLine({ main, sub }) {
  return (
    <Box>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {main || "—"}
      </Typography>
      {sub && (
        <Typography variant="caption" color="text.secondary">
          {sub}
        </Typography>
      )}
    </Box>
  );
}
